import React, { useEffect } from 'react';
import SEO from './SEO';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import './LegalPages.css';

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Privacy Policy - myCleanStudio"
        description="Learn how myCleanStudio protects your privacy and handles your data. Read our comprehensive privacy policy covering data collection, usage, and security."
        keywords="privacy policy, data protection, privacy, myCleanStudio privacy"
        url="/privacy"
      />
      <div className="legal-page">
        {/* Header */}
        <MyCleanStudioHeader />
      <div className="legal-container">

        {/* Content */}
        <main className="legal-content">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: September 2026</p>

          <section>
            <h2>Introduction</h2>
            <p>
              myCleanStudio ("we," "our," or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, and safeguard your information
              when you use our content moderation service.
            </p>
          </section>

          <section>
            <h2>Information We Collect</h2>

            <h3>Content You Submit</h3>
            <ul>
              <li><strong>Video Files:</strong> Videos you upload for compliance analysis</li>
              <li><strong>Transcripts:</strong> Text content you submit for analysis</li>
              <li><strong>Titles:</strong> Names you assign to your content checks</li>
            </ul>

            <h3>Account Information</h3>
            <p>
              When you sign in with Google, we receive:
            </p>
            <ul>
              <li>Your name and email address</li>
              <li>Profile picture (if available)</li>
              <li>Google account identifier</li>
            </ul>

            <h3>Payment Information</h3>
            <p>
              If you buy credits, your payment is processed by Stripe. We do not receive or
              store your card number. We keep only:
            </p>
            <ul>
              <li>A Stripe customer identifier, so we can associate a purchase with your account</li>
              <li>Your credit balance and a record of credits purchased and used</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Usage data (features used, timestamps)</li>
            </ul>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Analyze your content for platform guideline compliance</li>
              <li>Detect potentially problematic visual content (nudity, violence, etc.)</li>
              <li>Generate compliance scores and recommendations</li>
              <li>Store your analysis history for your reference</li>
              <li>Improve our service and analysis accuracy</li>
            </ul>
          </section>

          <section>
            <h2>How We Process Your Content</h2>

            <h3>Video Analysis</h3>
            <p>
              When you upload a video, we:
            </p>
            <ul>
              <li>Extract frames at regular intervals for visual analysis</li>
              <li>Analyze visual content to detect potentially sensitive material</li>
              <li>Extract and analyze audio transcripts</li>
              <li>Generate a compliance report with timestamps</li>
            </ul>

            <h3>Transcript Analysis</h3>
            <p>
              When you submit a transcript, we analyze it using AI to identify:
            </p>
            <ul>
              <li>Content that may violate platform guidelines</li>
              <li>Potentially sensitive language or topics</li>
              <li>Areas that may affect monetization</li>
            </ul>
          </section>

          <section>
            <h2>Data Storage and Retention</h2>
            <ul>
              <li><strong>Uploaded Videos:</strong> Stored temporarily during processing, then deleted</li>
              <li><strong>Flagged Frames:</strong> Stored for reference in your analysis history</li>
              <li><strong>Analysis Results:</strong> Retained for 90 days, then automatically deleted</li>
              <li><strong>Account Data:</strong> Retained while your account is active</li>
            </ul>

            <h3>Content Library (Logged-in Users)</h3>
            <p>
              As a logged-in user, you can save content to your library for future re-scans.
              This allows you to track compliance over time and re-analyze content when platform
              guidelines change. You have full control over your saved content and can:
            </p>
            <ul>
              <li>Delete individual content items from your library at any time</li>
              <li>Clear your entire scan history</li>
              <li>Remove all associated data including analysis results and flagged frames</li>
            </ul>
            <p>
              When you delete content from your library, all associated data is permanently removed
              from our systems.
            </p>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <p>We use the following third-party services to provide our functionality:</p>
            <ul>
              <li><strong>Amazon Web Services (AWS):</strong> Cloud infrastructure, storage, and automated image moderation</li>
              <li><strong>Google:</strong> Authentication services (OAuth)</li>
              <li><strong>OpenAI:</strong> Analysis of your transcripts and on-screen text to produce
                  compliance results. Content you submit for a scan is sent to OpenAI for processing.</li>
              <li><strong>ACRCloud:</strong> Music and audio recognition. Short audio samples from your
                  video are sent to ACRCloud to identify copyrighted music.</li>
              <li><strong>Stripe:</strong> Payment processing for credit purchases. Stripe collects and
                  stores your payment details directly &mdash; we never receive or store your full card
                  number.</li>
            </ul>
            <p>
              These services have their own privacy policies governing their use of data.
            </p>
          </section>

          <section>
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data:
            </p>
            <ul>
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure cloud storage with access controls</li>
              <li>Time-limited access URLs for media content</li>
              <li>Regular security reviews and updates</li>
            </ul>
          </section>

          <section>
            <h2>Your Rights and Controls</h2>
            <p>You have full control over your data. As a user, you can:</p>
            <ul>
              <li>Access your personal data and analysis history</li>
              <li>Delete individual content items from your library</li>
              <li>Clear your entire scan history at any time</li>
              <li>Request correction of inaccurate data</li>
              <li>Export your analysis history</li>
            </ul>
            <p>
              All deletion actions are immediate and permanent. We do not retain backup copies
              of deleted user content.
            </p>
            <p>
              To delete your account and all associated data, please email us at{' '}
              <a href="mailto:support@mycleanstudio.com">support@mycleanstudio.com</a>.
            </p>
          </section>

          <section>
            <h2>Cookies and Local Storage</h2>
            <p>
              We use local storage to maintain your session and preferences.
              We do not use third-party tracking cookies.
            </p>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              Our service is not intended for users under 13 years of age.
              We do not knowingly collect information from children under 13.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you
              of any changes by posting the new policy on this page and updating the
              "Last updated" date.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <p>
              <a href="mailto:support@mycleanstudio.com">support@mycleanstudio.com</a>
            </p>
          </section>
        </main>

      </div>
        {/* Footer */}
        <MyCleanStudioFooter />
      </div>
    </>
  );
}

export default PrivacyPolicy;
